import React from "react";
import ReactMarkdown from "react-markdown";
import { Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import { Components } from "react-markdown";

// Register fonts for PDF rendering
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import italic from "@packages/assets/font-family/Montserrat-Italic.ttf";
import boldItalic from "@packages/assets/font-family/Montserrat-BoldItalic.ttf";
// import emoji from "@packages/assets/font-family/NotoEmoji-Regular.ttf";
import emojiRegex from "emoji-regex";
import remarkGfm from "remark-gfm";
import firaCode from "@packages/assets/font-family/FiraCode-Regular.ttf";
import { Style } from "@react-pdf/types";

Font.register({
  family: "Montserrat",
  fonts: [
    { src: regular, fontWeight: "normal" },
    { src: bold, fontWeight: "bold" },
    { src: italic, fontWeight: "normal", fontStyle: "italic" },
    { src: boldItalic, fontWeight: "bold", fontStyle: "italic" },
  ],
});

Font.register({
  family: "FiraCode",
  fonts: [{ src: firaCode, fontWeight: "normal" }],
});

// Register the emoji font separately

// Font.registerEmojiSource({
//   format: "png",
//   url: "https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/",
// });
// Font.registerEmojiSource({
//   url: "https://cdn.jsdelivr.net/npm/emojione-assets@4.5/png/128/",
//   format: "png",
// });

// Font.register({
//   family: "NotoEmoji",
//   src: emoji,
//   fonts: [
//     { src: emoji, fontWeight: "normal" },
//     { src: emoji, fontWeight: "bold" },
//     { src: emoji, fontStyle: "italic" },
//     { src: emoji, fontWeight: "bold", fontStyle: "italic" },
//     { src: emoji, fontWeight: "normal", fontStyle: "italic" },
//     { src: emoji, fontWeight: "bold", fontStyle: "italic" },
//     { src: emoji, fontWeight: "normal", fontStyle: "italic" },
//     { src: emoji, fontWeight: "bold", fontStyle: "italic" },
//     { src: emoji, fontWeight: "normal", fontStyle: "italic" },
//     { src: emoji, fontWeight: "bold", fontStyle: "italic" },
//     { src: emoji, fontWeight: "normal", fontStyle: "italic" },
//     { src: emoji, fontWeight: "bold", fontStyle: "italic" },
//   ],
// });

const styles = StyleSheet.create({
  table: {
    display: "flex",
    width: "100%", // Ensure full width
    borderWidth: 1,
    borderColor: "#ccc",
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
  },
  tableCell: {
    flex: 1, // Ensures equal width
    padding: 6,
    fontSize: 10,
    borderRightWidth: 1,
    borderRightColor: "#ccc",
    textAlign: "center", // Center text
    fontFamily: "Montserrat",
  },
  tableHeader: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
    fontFamily: "Montserrat",
  },
  lastCell: {
    borderRightWidth: 0, // Remove border on last column
  },

  // Other styles
  paragraph: {
    marginBottom: 6,
    fontFamily: "Montserrat",
    fontSize: 10,
  },
  bold: {
    fontWeight: "bold",
    fontFamily: "Montserrat",
  },
  italic: {
    fontStyle: "italic",
    fontFamily: "Montserrat",
  },
  code: {
    fontFamily: "FiraCode",
    backgroundColor: "#f0f0f0",
    padding: 2,
    borderRadius: 3,
    fontSize: 9,
  },
  heading: {
    fontWeight: "bold",
    marginBottom: 4,
    fontSize: 14,
    fontFamily: "Montserrat",
  },
  listItem: {
    flexDirection: "row",
    marginBottom: 4, // controls spacing between list items
    alignItems: "flex-start",
  },

  listItemBullet: {
    width: 10,
    marginRight: 5,
  },
  listItemContent: {
    flex: 1,
    fontFamily: "Montserrat",
    fontSize: 10,
  },
  blockquote: {
    borderLeftWidth: 2,
    borderLeftColor: "#888",
    paddingLeft: 8,
    marginVertical: 8,
    marginLeft: 4,
  },
  blockquoteText: {
    fontStyle: "italic",
    fontFamily: "Montserrat",
    fontSize: 10,
    color: "#444",
  },
  container: {
    fontFamily: "Montserrat",
  },
});

interface MarkdownToPdfProps {
  children: string;
  globalStyle?: Style;
}

const MarkdownToPdf: React.FC<MarkdownToPdfProps> = ({
  children,
  globalStyle = {},
}) => {
  // const renderWithEmojiSupport = (text: string) => {
  //   if (!text) return null;

  //   const regex = emojiRegex(); // handles complex emoji sequences
  //   const parts: string[] = [];
  //   let lastIndex = 0;

  //   for (const match of text.matchAll(regex)) {
  //     const matchStart = match.index!;
  //     const matchEnd = matchStart + match[0].length;

  //     if (matchStart > lastIndex) {
  //       parts.push(text.slice(lastIndex, matchStart)); // normal text
  //     }

  //     parts.push(match[0]); // emoji
  //     lastIndex = matchEnd;
  //   }

  //   if (lastIndex < text.length) {
  //     parts.push(text.slice(lastIndex)); // remaining normal text
  //   }

  //   return (
  //     <Text>
  //       {parts.map((part, index) => {
  //         const isEmoji = emojiRegex().test(part);
  //         return (
  //           <Text
  //             key={index}
  //             style={{ fontFamily: isEmoji ? "NotoEmoji" : "Montserrat" }}
  //           >
  //             {part}
  //           </Text>
  //         );
  //       })}
  //     </Text>
  //   );
  // };
  const filterEmojis = (text: string): string => {
    return text.replace(emojiRegex(), "");
  };
  const components: Components = {
    // Paragraphs
    p: ({ children }) => {
      const cleaned = React.Children.toArray(children).map((child, index) => {
        if (typeof child === "string") {
          return <Text key={index}>{filterEmojis(child)}</Text>;
        }
        return child;
      });

      return <Text style={[styles.paragraph, globalStyle]}>{cleaned}</Text>;
    },

    // Bold
    strong: ({ children }) => {
      const cleaned = React.Children.toArray(children).map((child, index) => {
        const text = typeof child === "string" ? filterEmojis(child) : child;
        return (
          <Text style={[styles.bold, globalStyle]} key={index}>
            {text}
          </Text>
        );
      });

      return <>{cleaned}</>;
    },

    // Italic
    em: ({ children }) => {
      const cleaned = React.Children.toArray(children).map((child, index) => {
        const text = typeof child === "string" ? filterEmojis(child) : child;
        return (
          <Text style={[styles.italic, globalStyle]} key={index}>
            {text}
          </Text>
        );
      });

      return <>{cleaned}</>;
    },

    // Inline code
    code: ({ children }) => {
      const cleaned = React.Children.toArray(children).map((child, index) => {
        const text = typeof child === "string" ? filterEmojis(child) : child;
        return (
          <Text style={[styles.code, globalStyle]} key={index}>
            {text}
          </Text>
        );
      });

      return <>{cleaned}</>;
    },

    // Headings
    h1: ({ children }) => (
      <Text style={[styles.heading, { fontSize: 24 }, globalStyle]}>
        {React.Children.toArray(children).map((child, index) =>
          typeof child === "string" ? (
            <Text key={index}>{filterEmojis(child)}</Text>
          ) : (
            child
          ),
        )}
      </Text>
    ),
    h2: ({ children }) => (
      <Text style={[styles.heading, { fontSize: 20 }, globalStyle]}>
        {React.Children.toArray(children).map((child, index) =>
          typeof child === "string" ? (
            <Text key={index}>{filterEmojis(child)}</Text>
          ) : (
            child
          ),
        )}
      </Text>
    ),
    h3: ({ children }) => (
      <Text style={[styles.heading, { fontSize: 16 }, globalStyle]}>
        {React.Children.toArray(children).map((child, index) =>
          typeof child === "string" ? (
            <Text key={index}>{filterEmojis(child)}</Text>
          ) : (
            child
          ),
        )}
      </Text>
    ),

    // Lists
    ul: ({ children }) => (
      <View style={{ flexDirection: "column", gap: 4 }}>{children}</View>
    ),
    ol: ({ children }) => (
      <View style={{ flexDirection: "column", gap: 4 }}>{children}</View>
    ),
    li: ({ children }) => (
      <View style={styles.listItem}>
        <Text style={styles.listItemBullet}>•</Text>
        <View style={[styles.listItemContent, globalStyle]}>
          {React.Children.toArray(children).map((child, index) =>
            typeof child === "string" ? (
              <Text key={index}>{filterEmojis(child)}</Text>
            ) : (
              <React.Fragment key={index}>{child}</React.Fragment>
            ),
          )}
        </View>
      </View>
    ),

    // Blockquotes
    blockquote: ({ children }) => (
      <View style={styles.blockquote}>
        <Text style={[styles.blockquoteText, globalStyle]}>
          {React.Children.toArray(children).map((child, index) =>
            typeof child === "string" ? (
              <Text key={index}>{filterEmojis(child)}</Text>
            ) : (
              child
            ),
          )}
        </Text>
      </View>
    ),

    // Line breaks
    br: () => <Text>{"\n"}</Text>,

    // Tables
    table: ({ children }) => <View style={styles.table}>{children}</View>,
    thead: ({ children }) => <View>{children}</View>,
    tbody: ({ children }) => <View>{children}</View>,
    tr: ({ children }) => <View style={styles.tableRow}>{children}</View>,
    th: ({ children }) => (
      <Text style={[styles.tableCell, styles.tableHeader, globalStyle]}>
        {React.Children.toArray(children).map((child, index) =>
          typeof child === "string" ? (
            <Text key={index}>{filterEmojis(child)}</Text>
          ) : (
            <React.Fragment key={index}>{child}</React.Fragment>
          ),
        )}
      </Text>
    ),

    td: ({ children }) => (
      <Text style={[styles.tableCell, globalStyle]}>
        {React.Children.toArray(children).map((child, index) =>
          typeof child === "string" ? (
            <Text key={index}>{filterEmojis(child)}</Text>
          ) : (
            <React.Fragment key={index}>{child}</React.Fragment>
          ),
        )}
      </Text>
    ),
  };

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children}
    </ReactMarkdown>
  );
};

export default MarkdownToPdf;
